import { IGif } from '@giphy/js-types'
import { Gif } from '@giphy/react-components'
import { Video, VideoPreviewLabel } from '@giphy/react-giphy-brand'
import React, { SyntheticEvent, useContext, useState, useCallback } from 'react'
import url from 'url'
import { useHistory } from 'react-router'
import PlayBoxIcon from 'shared/components/icons/video/play'
import VideoPlayerAnalytics from 'shared/components/video-player/analytics'
import { mobile } from 'shared/util/media-query'
import styled, { css } from 'styled-components'
import ModalContext from '../context/modal'
import UserContext from '../context/user'
import { isPlaceholder } from '../gif-skeleton'
import { EditButton } from './edit-button'
import Lock from 'shared/components/icons/lock'
import { giphyRed } from '@giphy/js-brand'
export const ASPECT_RATIO = 16 / 9

const Container = styled.div<{ isPlaceholder: boolean }>`
    align-items: center;
    display: flex;
    flex-direction: column;

    ${props =>
        props.isPlaceholder &&
        css`
            visibility: hidden;
            pointer-events: none;
        `}
`

const OverlayContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    height: 100%;
    width: 100%;
    opacity: 0;
`

const Private = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    fill: ${giphyRed};
    background: rgba(0, 0, 0, 0.5);
    pointer-events: none;
    transition: opacity 0.2s ease-out 0s;
`

const PreviewContainer = styled.div`
    position: relative;
    .${Gif.className} {
        img {
            object-fit: cover;
        }
    }
    display: flex;

    &:hover {
        ${OverlayContainer} {
            opacity: 1;
        }

        ${Private} {
            opacity: 0;
        }
    }
`
const StyledPreviewLabel = styled(VideoPreviewLabel)`
    margin-left: auto;
    margin-right: auto;

    h3 {
        font-size: 16px;
    }
`

const PlayIcon = styled(PlayBoxIcon)`
    position: absolute;
    bottom: 10px;
    left: 10px;
`

const StyledEditButton = styled(EditButton)`
    position: absolute;
    bottom: 6px;
    right: 6px;

    ${mobile.css`
        display: none;
    `}
`

type Props = {
    gif: IGif
    width: number
    height?: number
    className?: string
    hideAttribution?: boolean
    editButtonSpriteUrl?: string // for storybook only
    labelPadding?: number
    canPlayInline?: boolean
    preventDefault?: boolean
    hideLabel?: boolean
    playButtonSize?: { height: number; width: number }
    playButtonAnimated?: boolean
}

const VideoPreview = ({
    gif,
    width,
    height,
    editButtonSpriteUrl,
    hideAttribution = false,
    className = '',
    labelPadding = 0,
    canPlayInline = false,
    preventDefault = true,
    hideLabel = false,
    playButtonSize = undefined,
    playButtonAnimated = false,
}: Props) => {
    const events: VideoPlayerAnalytics = new VideoPlayerAnalytics(gif.id)
    const [isPlayingInline, setIsPlayingInline] = useState(false)
    const { user: loggedInUser } = useContext(UserContext)
    const { openGifEditPanel } = useContext(ModalContext)
    const history = useHistory()
    const { title, user, is_hidden } = gif
    const canEditGif = (loggedInUser && loggedInUser.isStaff) || (loggedInUser && user && user.id === loggedInUser.id)
    const previewHeight = height || width / ASPECT_RATIO
    const { path } = url.parse(gif.url)
    const onEditButtonClicked = (evt: SyntheticEvent<HTMLElement, Event>) => {
        evt.preventDefault()
        openGifEditPanel(gif)
    }
    const onPlayButtonClicked = () => {
        if (canPlayInline) {
            setIsPlayingInline(true)
        }
    }
    const Overlay = () => (
        <OverlayContainer>
            {canEditGif && <StyledEditButton background={editButtonSpriteUrl} onClick={onEditButtonClicked} />}
        </OverlayContainer>
    )
    const PlayButton = useCallback(() => {
        if (!canPlayInline) {
            return (
                <a href={String(path)}>
                    <PlayIcon onClick={onPlayButtonClicked} size={playButtonSize} isAnimated={playButtonAnimated} />
                </a>
            )
        }
        return <PlayIcon onClick={onPlayButtonClicked} size={playButtonSize} isAnimated={playButtonAnimated} />
    }, [path, onPlayButtonClicked, playButtonSize, canPlayInline])
    return (
        <Container className={className} isPlaceholder={!!gif[isPlaceholder]}>
            <PreviewContainer>
                {isPlayingInline ? (
                    <Video gif={gif} width={width} height={height} autoPlay events={events} noCutout noShare />
                ) : (
                    <>
                        <Gif
                            gif={gif}
                            width={width}
                            onGifClick={(_, e) => {
                                if (preventDefault) {
                                    e.preventDefault()
                                    history.push(String(path))
                                }
                            }}
                            height={previewHeight}
                            overlay={Overlay}
                        />
                        <PlayButton />
                        {!!is_hidden && (
                            <Private>
                                <Lock />
                            </Private>
                        )}
                    </>
                )}
            </PreviewContainer>
            {!hideLabel && (
                <StyledPreviewLabel
                    href={gif.url}
                    onClick={() => history.push(String(path))}
                    showAttribution={!hideAttribution}
                    title={title}
                    user={user}
                    width={width - labelPadding * 2}
                />
            )}
        </Container>
    )
}

export default VideoPreview
